import { Button } from 'admin/components/atoms/Buttons';
import ConfirmModal from 'admin/components/atoms/ConfirmModal';
import Select from 'admin/components/atoms/Select';
import TextArea from 'admin/components/atoms/TextArea';
import TextBox from 'admin/components/atoms/TextBox';
import ToggleSwitch from 'admin/components/atoms/ToggleSwitch';
import ApiResults from 'admin/components/molecules/ApiResults';
import BreadCrumb from 'admin/components/molecules/BreadCrumb';
import usePartnerClient from 'admin/hooks/usePartnerClient';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import styled, { css } from 'styled-components';
import Headings from '../../atoms/Headings';
import PartnerClientConfirmModal from './PartnerClientConfirmModal';
import { PartnerClientHeaders, Row, RowCell } from './PartnerClientHeaders';
import { usePartnerClientForm } from './usePartnerClientForm';

const FormWrapper = styled.div`
  ${TextBox} {
    width: 500px;
  }
  ${Select} {
    width: 500px;
  }
  ${TextArea} {
    width: 500px;
    resize: both;
  }
`;

const PageTitle = styled(Headings.h2)`
  ${({ theme }) => css`
    ${theme.typography.heading3.bold}
    color: ${theme.colors.gray[300]};
  `}
`;

const SubLabel = styled.span`
  display: inline-block;
  width: 100px;
`;

const PartnerClientDetail = ({ match }) => {
  const crumbs = [
    { value: 'ホーム', href: '/', isCurrentPage: false },
    { value: 'サービス管理', href: '', isCurrentPage: true },
    { value: 'PartnerClient', href: '/service-settings/partner-clients', isCurrentPage: false },
    { value: 'PartnerClient詳細', href: '', isCurrentPage: true },
  ];

  const partnerClientId = match.params.id;
  const { partnerClient, submitUpdate } = usePartnerClient(partnerClientId);
  const { form, handleChangeForm } = usePartnerClientForm(partnerClient);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [updateClientSecret, setUpdateClientSecret] = useState(false);
  const [apiResult, setApiResult] = useState(null);

  const openModal = (e) => {
    e.preventDefault();
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const onSuccess = () => {
    setApiResult({ text: 'PartnerClientは正常に更新されました', isError: false });
  };

  const registerPartnerClient = () => {
    let bodyParams = {
      name: form.name,
      description: form.description,
      status: form.status,
    };

    if (updateClientSecret === true) {
      bodyParams = Object.assign({}, bodyParams, { is_regenerate_secret: 'true' });
      setUpdateClientSecret(false);
    }

    submitUpdate(form.id, bodyParams, onSuccess);

    closeModal();
  };

  return (
    <FormWrapper>
      <BreadCrumb crumbs={crumbs} />

      <PageTitle>PartnerClient詳細</PageTitle>

      <form onSubmit={openModal}>
        <Row>
          <PartnerClientHeaders keyName="id" />
          <RowCell>{form.id}</RowCell>
        </Row>
        <Row>
          <PartnerClientHeaders keyName="organization_and_user" />
          <RowCell>
            <div>
              <SubLabel>組織</SubLabel>：
              {form.organization !== null ? form.organization.name : null}
            </div>
            <div>
              <SubLabel>管理ユーザ</SubLabel>：
              {form.admin_user !== null ? form.admin_user.name : null}
            </div>
          </RowCell>
        </Row>
        <Row>
          <PartnerClientHeaders keyName="name" />
          <RowCell>
            <TextBox
              value={form.name}
              onBlur={handleChangeForm('name', (v) => v.target.value)}
              required
            />
          </RowCell>
        </Row>
        <Row>
          <PartnerClientHeaders keyName="client_secret" />
          <RowCell>
            {form.client_secret}
            <div>
              <ToggleSwitch
                checkedText="更新"
                uncheckedText=""
                height={22}
                width={50}
                checked={updateClientSecret}
                onChange={setUpdateClientSecret}
              />
            </div>
          </RowCell>
        </Row>
        <Row>
          <PartnerClientHeaders keyName="description" />
          <RowCell>
            <TextArea
              cols="90"
              value={form.description || ''}
              onChange={handleChangeForm('description', (v) => v.currentTarget.value)}
              maxLength={300}
              placeholder="PartnerClientの説明文を記載"
            />
          </RowCell>
        </Row>
        <Row>
          <PartnerClientHeaders keyName="status" />
          <RowCell>
            <ToggleSwitch
              checked={form.status === 'ENABLED'}
              onChange={handleChangeForm('status', (v) => (v ? 'ENABLED' : 'DISABLED'))}
            />
          </RowCell>
        </Row>
        <Button>更新</Button>
      </form>
      <ConfirmModal
        isModalOpen={isModalOpen}
        closeModal={closeModal}
        title="更新内容の確認"
        form={
          <PartnerClientConfirmModal
            form={form}
            onConfirm={registerPartnerClient}
            onCancel={closeModal}
            updateClientSecret={updateClientSecret}
            isUpdate
          />
        }
      />
      {apiResult ? <ApiResults messages={[apiResult]} /> : null}
    </FormWrapper>
  );
};

PartnerClientDetail.propTypes = {
  match: PropTypes.object.isRequired,
};

export default PartnerClientDetail;
